import jsCookies from 'js-cookie';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { type ComponentType, useEffect, useMemo, useState } from 'react';
import PageLoading from 'src/components/common/page-loading/page-loading';
import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import { routes } from 'src/constants/navigation';
import { useAnalytics } from 'src/features/Analytics/hooks';
import { useGetAbTests } from 'src/features/Analytics/hooks/useGetAbTests';
import { useAbortableEffect } from 'src/utils/hooks/use-abortable';
const page = <T extends object,>(Component: ComponentType<T>) => observer((props: T) => {
  const [mounted, setMounted] = useState(false);
  const {
    authStore: {
      user,
      variant,
      fetchUser
    },
    analyticsStore
  } = useStores();
  const {
    saveAbTest
  } = analyticsStore;
  const router = useRouter();
  const {
    route,
    query,
    isReady
  } = router;
  const googlePseudoIdCurrent = jsCookies.get('_ga')?.slice(6);
  const emailToken = typeof query['email_token'] === 'string' ? query['email_token'] : undefined;
  const {
    trackGoogleEvent
  } = useAnalytics();
  const result = useMemo(() => {
    if (!route.includes(routes.quiz) && (!mounted || user === undefined)) {
      return <PageLoading />;
    } else {
      return <Component {...props} />;
    }
  }, [route, mounted, user, Component, props]);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    router.prefetch(routes.quiz).then(r => r);
    // router.prefetch(routes.newPlanPaywall).then((r) => r);
  }, [router]);
  useAbortableEffect(() => {
    if (emailToken === undefined || variant !== 'emailtraf') {
      fetchUser();
    }
  }, [emailToken, fetchUser]);

  // Получаем ab тесты через новый хук
  const {
    data: abTests,
    error: abTestsError,
    isSuccess: abTestsSuccess,
    fetchStatus: abTestsFetchStatus
  } = useGetAbTests({
    variant: variant ?? '',
    utm_source: query['utm_source'] as string ?? '',
    utm_campaign: query['utm_campaign'] as string ?? '',
    region: query['region'] as string ?? '',
    user_id: googlePseudoIdCurrent,
    enabled: !analyticsStore.segment && isReady && query['reset'] === undefined && query['hard_reset'] === undefined
  });
  useEffect(() => {
    // Wait for a refetch so a previous session's cached case is not locked in.
    // Read the live session store so effect replays cannot report twice.
    if (analyticsStore.segment || abTestsFetchStatus !== 'idle' || !isReady || query['reset'] !== undefined || query['hard_reset'] !== undefined) {
      return;
    }
    if (abTests && abTestsSuccess) {
      if (Object.keys(abTests).length === 0) {
        saveAbTest('default', undefined);
        trackGoogleEvent({
          eventName: 'segment_name',
          options: {
            name: 'default'
          }
        });
      } else {
        saveAbTest(abTests.segment_name, abTests.tests);
        trackGoogleEvent({
          eventName: 'segment_name',
          options: {
            name: String(abTests.segment_name)
          }
        });
        for (const test of abTests.tests) {
          trackGoogleEvent({
            eventName: 'ab_test',
            options: {
              name: String(test.test_name),
              group: String(test.case_name)
            }
          });
        }
      }
    } else if (abTestsError) {
      saveAbTest('error', undefined);
      trackGoogleEvent({
        eventName: 'segment_name',
        options: {
          name: 'error'
        }
      });
    }
  }, [abTests, abTestsError, abTestsFetchStatus, analyticsStore, analyticsStore.segment, saveAbTest, trackGoogleEvent, abTestsSuccess, isReady, query]);

  // The check on `mounted` helps avoid hydration DOM mismatch
  // that happens because `user` is always empty during SSR,
  // but may be `null` if the user is not logged in
  return result;
});
export default page;